import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import type { PetResponse } from 'we-tree-api-client/v2';
import { petCategory } from '@/translations/en';
import { PET_OWNER_ROLE_LABELS, type PetOwnerRoleValue } from '@/types/pet-owner';

export interface AddPetLinkExistingFieldsProps {
  linkPetsLoading: boolean;
  linkablePets: PetResponse[];
  linkPetFamilyMemberId: string;
  onSelectPet: (petFamilyMemberId: string) => void;
  linkOwnerRole: PetOwnerRoleValue;
  onLinkOwnerRoleChange: (role: PetOwnerRoleValue) => void;
  linkRoleOptions: PetOwnerRoleValue[];
  isSubmitting: boolean;
}

const AddPetLinkExistingFields: React.FC<AddPetLinkExistingFieldsProps> = ({
  linkPetsLoading,
  linkablePets,
  linkPetFamilyMemberId,
  onSelectPet,
  linkOwnerRole,
  onLinkOwnerRoleChange,
  linkRoleOptions,
  isSubmitting
}) => {
  const { t } = useTranslation('forms');
  const { t: tScreens } = useTranslation('screens');
  const { t: tCommon } = useTranslation('common');

  return (
    <div className="add-pet-page__link-existing">
      <p className="add-flow-entry-hint">
        Choose a pet already in this family that is not yet listed under this person as an owner.
      </p>
      {linkPetsLoading ? (
        <p className="person-edit-form__hint">{tScreens('addFlow.loadingPets')}</p>
      ) : linkablePets.length === 0 ? (
        <p className="person-edit-form__hint">
          No pets available to link. Every pet in the family may already be linked to this person.
        </p>
      ) : (
        <>
          <div className="add-flow-link-field">
            <span className="person-edit-form__label" id="add-pet-link-select-label">
              {tCommon('pet')}
            </span>
            <Select.Root value={linkPetFamilyMemberId} onValueChange={onSelectPet} disabled={isSubmitting}>
              <Select.Trigger className="form-select-trigger" aria-labelledby="add-pet-link-select-label">
                <Select.Value placeholder={t('pet.selectPet')} />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {linkablePets.map((p) => (
                    <Select.Item key={p.familyMemberId} value={p.familyMemberId} className="form-select-item">
                      <Select.ItemText>
                        {p.displayName}
                        {p.category ? ` · ${petCategory[p.category] ?? p.category}` : ''}
                      </Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
          {linkPetFamilyMemberId && (
            <div className="add-flow-link-field">
              <span className="person-edit-form__label" id="add-pet-link-role-label">
                {t('pet.yourRoleAsOwner')}
              </span>
              <Select.Root
                value={linkOwnerRole}
                onValueChange={(v) => onLinkOwnerRoleChange(v as PetOwnerRoleValue)}
                disabled={isSubmitting}
              >
                <Select.Trigger className="form-select-trigger" aria-labelledby="add-pet-link-role-label">
                  <Select.Value />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {linkRoleOptions.map((r) => (
                      <Select.Item key={r} value={r} className="form-select-item">
                        <Select.ItemText>{PET_OWNER_ROLE_LABELS[r]}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddPetLinkExistingFields;
